import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, unref } from 'vue'

import { useAuthService } from '@/authuser/services.js'
import api from '@/group/api/groups'
import sanctionsAPI from '@/group/api/sanctions'
import { useGroupSanction } from '@/group/helpers.js'
import { useSanctionService } from '@/group/services.js'
import { queryKeyUserListAll } from '@/users/queries.js'
import { useSocketEvents } from '@/utils/composables'
import { useQueryHelpers, useWait } from '@/utils/queryHelpers'

export const QUERY_KEY_BASE = 'groups'
export const queryKeyGroupDetail = groupId => [QUERY_KEY_BASE, 'detail', groupId].filter(Boolean)
export const queryKeyTimezones = () => [QUERY_KEY_BASE, 'timezones']
export const queryKeySanctions = () => [QUERY_KEY_BASE, 'sanctions']

// Take a query key, return group id if it's a group detail query key
export function parseGroupDetailQueryKey (queryKey) {
  if (queryKey[0] !== QUERY_KEY_BASE && queryKey[1] !== 'detail') {
    return
  }
  return queryKey[2]
}

export function useGroupDetailUpdater () {
  const queryClient = useQueryClient()
  const { maybeUpdateDataWith } = useQueryHelpers()
  const { on } = useSocketEvents()
  on('groups:group_detail', updatedGroup => {
    queryClient.setQueryData(
      queryKeyGroupDetail(updatedGroup.id),
      maybeUpdateDataWith(updatedGroup),
    )
  })
}

export function useSanctionsUpdater () {
  const queryClient = useQueryClient()
  const { on } = useSocketEvents()
  const { updateOrInvalidateListEntry } = useQueryHelpers()

  on('sanctions:sanction', updatedEntry => {
    updateOrInvalidateListEntry(queryKeySanctions(), updatedEntry)
    if (updatedEntry.type === 'group') {
      queryClient.invalidateQueries({ queryKey: queryKeyUserListAll() })
    }
  })
}

export function useGroupDetailQuery ({ groupId }, queryOptions = {}) {
  const { isFetched: sanctionsFetched } = useSanctionService()
  const groupSanction = useGroupSanction(groupId)

  const query = useQuery({
    queryKey: queryKeyGroupDetail(groupId),
    queryFn: () => api.get(unref(groupId)), // TODO: this can call api.get(null) :/
    // Only allow the group if we have a group id and it's not sanctioned...
    // Means we avoid a 404 that we know is coming
    // Also means if the sanction goes away, we'll magically fetch the group
    enabled: computed(() => Boolean(unref(groupId)) && Boolean(sanctionsFetched.value) && !groupSanction.value),
    staleTime: Infinity,
    ...queryOptions,
  })
  return {
    ...query,
    wait: useWait(query),
    group: query.data,
  }
}

export function useTimezonesQuery () {
  const query = useQuery({
    queryKey: queryKeyTimezones(),
    queryFn: () => api.timezones(),
    placeholderData: () => ({ allTimezones: [] }),
    staleTime: Infinity, // they don't change much...
    select: data => data.allTimezones,
  })
  return {
    ...query,
    timezones: query.data,
  }
}

export function useSanctionsQuery () {
  const { isLoggedIn } = useAuthService()
  const query = useQuery({
    queryKey: queryKeySanctions(),
    queryFn: () => sanctionsAPI.list(),
    enabled: isLoggedIn,
    placeholderData: () => [],
    staleTime: Infinity,
  })
  return {
    ...query,
    sanctions: query.data,
    wait: useWait(query),
  }
}
